import React, { useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * Formats a phone number as (999) 999 9999
 */
const formatPhoneNumber = (value) => {
  const digits = (value || '').replace(/\D/g, '').substring(0, 10);
  const len = digits.length;

  if (len === 0) return '';
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

/**
 * Gets the digit count up to a position in formatted string
 */
const getDigitCountUpToPosition = (formattedValue, position) => {
  let count = 0;
  for (let i = 0; i < position && i < formattedValue.length; i++) {
    if (/\d/.test(formattedValue[i])) {
      count++;
    }
  }
  return count;
};

/**
 * Gets the cursor position for a given digit count in formatted string
 */
const getCursorPositionForDigitCount = (formattedValue, digitCount) => {
  let count = 0;
  for (let i = 0; i < formattedValue.length; i++) {
    if (/\d/.test(formattedValue[i])) {
      count++;
      if (count === digitCount) {
        return i + 1;
      }
    }
  }
  return formattedValue.length;
};

/**
 * PhoneInput component with proper cursor management
 */
const PhoneInput = ({ name, label, required = false, className = '', hasApiError = false }) => {
  const inputRef = useRef(null);
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { ref, ...rest } = register(name, {
    required: required ? `${label} is required` : false,
    validate: (value) => {
      if (!required && !value) return true;
      const digits = (value || '').replace(/\D/g, '');
      return digits.length === 10 || 'Please enter a valid 10-digit number';
    },
  });

  const handleChange = useCallback(
    (e) => {
      const input = e.target;
      const oldValue = input.value;
      const cursorPos = input.selectionStart || 0;

      // Count digits before cursor in old value
      const digitsBeforeCursor = getDigitCountUpToPosition(oldValue, cursorPos);

      // Format the new value
      const newValue = formatPhoneNumber(oldValue);

      // Update form value
      setValue(name, newValue, { shouldValidate: true, shouldDirty: true });

      // Calculate new cursor position
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const newCursorPos = getCursorPositionForDigitCount(newValue, digitsBeforeCursor);
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    },
    [name, setValue]
  );

  const handleKeyDown = useCallback((e) => {
    const input = e.target;
    const cursorPos = input.selectionStart || 0;
    const value = input.value;

    // Handle backspace at formatting characters
    if (e.key === 'Backspace' && cursorPos > 0) {
      const charBefore = value[cursorPos - 1];
      // If cursor is after a non-digit, move cursor back to skip it
      if (!/\d/.test(charBefore)) {
        e.preventDefault();
        // Find the previous digit position
        let newPos = cursorPos - 1;
        while (newPos > 0 && !/\d/.test(value[newPos - 1])) {
          newPos--;
        }
        if (newPos > 0) {
          // Delete the digit before the formatting characters
          const digits = value.replace(/\D/g, '');
          const digitIndex = getDigitCountUpToPosition(value, newPos) - 1;
          if (digitIndex >= 0) {
            const newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
            const newValue = formatPhoneNumber(newDigits);
            input.value = newValue;
            setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
            const newCursorPos = getCursorPositionForDigitCount(newValue, digitIndex);
            input.setSelectionRange(newCursorPos, newCursorPos);
          }
        }
      }
    }

    // Handle delete at formatting characters
    if (e.key === 'Delete' && cursorPos < value.length) {
      const charAfter = value[cursorPos];
      if (!/\d/.test(charAfter)) {
        e.preventDefault();
        // Find the next digit position
        let nextDigitPos = cursorPos;
        while (nextDigitPos < value.length && !/\d/.test(value[nextDigitPos])) {
          nextDigitPos++;
        }
        if (nextDigitPos < value.length) {
          const digits = value.replace(/\D/g, '');
          const digitIndex = getDigitCountUpToPosition(value, nextDigitPos + 1) - 1;
          if (digitIndex >= 0 && digitIndex < digits.length) {
            const newDigits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
            const newValue = formatPhoneNumber(newDigits);
            input.value = newValue;
            setValue(name, newValue, { shouldValidate: true, shouldDirty: true });
            const newCursorPos = getCursorPositionForDigitCount(newValue, digitIndex);
            input.setSelectionRange(newCursorPos, newCursorPos);
          }
        }
      }
    }
  }, [name, setValue]);

  const setRefs = useCallback(
    (element) => {
      ref(element);
      inputRef.current = element;
    },
    [ref]
  );

  const error = errors[name];
  const hasError = error || hasApiError;
  const borderClass = hasError ? 'border-danger-500' : 'border-secondary-500';

  return (
    <div className="flex flex-col w-full">
      <label htmlFor={name} className="text-text-hint mb-1">
        {label}
        {required && <span className="text-danger-500">*</span>}
      </label>
      <input
        {...rest}
        ref={setRefs}
        type="text"
        id={name}
        maxLength={14}
        placeholder="(999) 999 9999"
        className={`outline-none border-2 ${borderClass} rounded-lg text-black pencialicon ${className}`}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {error && <span className="text-danger-500">{error.message}</span>}
    </div>
  );
};

export default PhoneInput;
